import { graphql } from 'gatsby';
import get from 'lodash.get';
import React from 'react';

import ProfileCard from '../components/AboutUs/ProfileCard';
import { ArrowLink } from '../components/Common/Links';
import OverlappingHero from '../components/Common/OverlappingHero';
import PageSection from '../components/Common/PageSection';
import PageSectionHalfBleed from '../components/Common/PageSectionHalfBleed';
import RichTextConverter from '../components/Common/RichText/RichTextConverter';
import SimpleCard from '../components/Common/SimpleCard';
import ThreeUp from '../components/Common/ThreeUp';
import IconList from '../components/Generic/IconList';
import Layout from '../components/Layouts/Layout';
import MarketingSeo from '../components/MarketingSeo';
import * as styles from './aboutUs.module.css';

const specialBG = '../images/aboutus/sectionBg.svg';

const AboutUs = ({ data: { page }, location }) => {
  return (
    <Layout pageMeta={page.pageMetaData}>
      <MarketingSeo meta={page.pageMetaData} slug={location.pathname} />
      <PageSection slim isTopSection={true} className={styles.topSection}>
        <OverlappingHero
          copy={page.headingIntroCopy}
          ctaUrl={page.headingCtaUrl}
          cta={page.headingCtaLabel}
          className={styles.imageAdjust}
          slim
          image={page.headingImage}
          heading={page.heading}
          headingCtas={page.headingCtAs}
        />
      </PageSection>
      {page.pageSections.map((section, i) => {
        switch (section.__typename) {
          case 'ContentfulSimpleCard': {
            if (!section.subtitle) {
              return (
                <PageSection slim key={i} className={styles.grey}>
                  <div className={styles.generalContent}>
                    <h3>{section.title}</h3>
                    <div>
                      <RichTextConverter copy={section.body} />
                    </div>
                  </div>
                </PageSection>
              );
            } else {
              return (
                <PageSection slim key={i} className={styles.secondGeneralContent}>
                  <h3 id={'inclusion'}>{section.title}</h3>
                  <div className={styles.generalContent}>
                    <h4>{section.subtitle}</h4>
                    <div>
                      <RichTextConverter copy={section.body} />
                    </div>
                  </div>
                </PageSection>
              );
            }
          }
          case 'ContentfulTestimonial': {
            return (
              <PageSectionHalfBleed
                slim
                key={i}
                body={section.quote?.quote}
                imageObj={section.attributionImage}
                bgColor={'#9473f0'}
                bgUrl={specialBG}
                className={styles.halfBleedOverride}
              />
            );
          }
          case 'ContentfulGroupedContent': {
            if (section.type === 'Three Up') {
              return <ThreeUp id={'watchwords'} section={section} key={i} />;
            } else if (section.type === 'Icon List') {
              return (
                <PageSection slim key={i}>
                  <IconList
                    id={'operating-principals'}
                    list={get(section, 'content', [])}
                    gatsbyImage={get(section, 'mainImage.gatsbyImageData')}
                    paragraph={get(section, 'sectionCopy')}
                  />
                </PageSection>
              );
            } else if (section.type === 'Large Cards') {
              return (
                <PageSection slim key={i}>
                  <div className={styles.largeCardsHeading}>
                    <h3 id={'investors'}>{section.title}</h3>
                    <RichTextConverter copy={section.sectionCopy} />
                  </div>
                  <div className={styles.grid}>
                    {(get(section, 'content', []) || []).map((card, i) => {
                      const imageData = get(card, 'image.gatsbyImageData');
                      const src = get(card, 'image.file.url');
                      return (
                        <SimpleCard
                          title={card.title}
                          body={get(card, 'body')}
                          imageSrc={src}
                          imageObj={imageData}
                          imageType={'full'}
                          short={true}
                        />
                      );
                    })}
                  </div>
                </PageSection>
              );
            } else if (section.type === 'Template Standard') {
              return (
                <PageSection slim key={i} className={styles.grey}>
                  <div className={styles.leadership}>
                    <RichTextConverter copy={section.sectionCopy} />
                    <div className={styles.leadershipGrid}>
                      {section.content?.map((card, i) => {
                        return (
                          <ProfileCard
                            name={card.title}
                            image={card.image}
                            copy={card.body}
                            link={card.linkUrl}
                            key={i}
                          />
                        );
                      })}
                    </div>
                  </div>
                </PageSection>
              );
            }
            break;
          }
          case 'ContentfulCallToAction': {
            return (
              <PageSection slim key={i} className={styles.whiteIce}>
                <h2>{section.description?.description}</h2>
                <ArrowLink className={styles.arrow} label={section.ctaLabel} url={section.url} />
              </PageSection>
            );
          }
          default: {
            return null;
          }
        }
        return null;
      })}
    </Layout>
  );
};

export default AboutUs;

export const query = graphql`
  query AboutUsTemplateQuery($slug: String!) {
    page: contentfulMultipurposePageTemplate(slug: { eq: $slug }) {
      ...MultipurposePage
    }
  }
`;
